// Module Vuex pour la gestion de l'authentification
// Traduit depuis apps/pwa/src/Reducer/Login.js
import authService from '../../services/authService'

const state = {
  username: '',
  password: '',
  tooltip: false,
  usernameError: '',
  passwordError: '',
  loading: false,
  isLoggedIn: false,
  isHeightExtended: false,
  user: null,
  isInitializing: false,
  initializePromise: null
}

const mutations = {
  CHANGE_USERNAME(state, username) {
    state.username = username
    state.usernameError = ''
  },
  
  CHANGE_PASSWORD(state, password) {
    state.password = password
  },
  
  TOGGLE_TOOLTIP(state) {
    state.tooltip = !state.tooltip
  },
  
  RESET_TOOLTIP(state) {
    state.tooltip = false
  },
  
  SEND_CREDENTIALS(state) {
    state.loading = true
    state.usernameError = ''
    state.passwordError = ''
  },
  
  GRANT_ACCESS(state, user) {
    state.isLoggedIn = true
    state.usernameError = ''
    state.passwordError = ''
    state.loading = false
    state.user = user
  },
  
  RESTORE_SESSION(state, user) {
    state.isLoggedIn = true
    state.user = user
    state.usernameError = ''
    state.passwordError = ''
    state.loading = false
  },
  
  DENY_ACCESS(state, errorMessage) {
    state.usernameError = errorMessage
    state.passwordError = errorMessage
    state.loading = false
    state.isLoggedIn = false
    state.user = null
  },
  
  LOGOUT(state) {
    state.isLoggedIn = false
    state.username = ''
    state.password = ''
    state.user = null
    state.usernameError = ''
    state.passwordError = ''
  },
  
  SET_USER(state, user) {
    state.user = user
    state.isLoggedIn = !!user
  },
  
  EXTEND_HEIGHT(state) {
    state.isHeightExtended = true
  },
  
  RESET_HEIGHT(state) {
    state.isHeightExtended = false
  },
  
  SET_INITIALIZING(state, value) {
    state.isInitializing = value
  },
  
  SET_INITIALIZE_PROMISE(state, promise) {
    state.initializePromise = promise
  }
}

const actions = {
  changeUsername({ commit }, username) {
    commit('CHANGE_USERNAME', username)
  },
  
  changePassword({ commit }, password) {
    commit('CHANGE_PASSWORD', password)
  },
  
  toggleTooltip({ commit }) {
    commit('TOGGLE_TOOLTIP')
  },
  
  resetTooltip({ commit }) {
    commit('RESET_TOOLTIP')
  },
  
  async sendCredentials({ commit, state, dispatch }) {
    commit('SEND_CREDENTIALS')
    
    try {
      const data = await authService.login(state.username, state.password)
      commit('GRANT_ACCESS', data.user)
      
      // Synchroniser avec le module user
      await dispatch('user/setCurrentUser', data.user, { root: true })
      
      // Initialiser les paramètres de session (team, manager) après connexion réussie
      try {
        const params = {}
        
        // Ajouter le team si disponible
        if (data.user.team && data.user.team.id) {
          params.team = data.user.team.id
        }
        
        // Ajouter le manager (user.id) si disponible
        if (data.user.id) {
          params.manager = data.user.id
        }
        
        // Ajouter les paramètres par défaut
        params.status = 'vacant'
        params.hasPlanification = 'ok'
        params.hasNotPlanification = 'ok'
        
        // Sauvegarder dans sessionStorage
        if (Object.keys(params).length > 0) {
          sessionStorage.setItem('preservedRouteParams', JSON.stringify(params))
        }
      } catch (sessionError) {
        console.error('Erreur lors de l\'initialisation des paramètres de session:', sessionError)
      }
      
      // Rediriger vers la page principale ou déclencher une navigation
      return { success: true, user: data.user }
    } catch (error) {
      commit('DENY_ACCESS', error.message || 'Erreur de connexion')
      // Nettoyer également le module user en cas d'erreur
      dispatch('user/clearUser', null, { root: true }).catch(() => {})
      return { success: false, error: error.message }
    }
  },
  
  async logout({ commit, dispatch }) {
    try {
      await authService.logout()
    } catch (error) {
      console.warn('Erreur lors de la déconnexion:', error)
    } finally {
      commit('LOGOUT')
      // Nettoyer également le module user
      dispatch('user/clearUser', null, { root: true }).catch(() => {})
      // Nettoyer les paramètres de session
      try {
        sessionStorage.removeItem('preservedRouteParams')
      } catch (sessionError) {
        console.error('Erreur lors du nettoyage des paramètres de session:', sessionError)
      }
    }
  },
  
  async initializeAuth({ commit, dispatch, state }) {
    // Éviter les appels multiples simultanés
    if (state.isInitializing && state.initializePromise) {
      return state.initializePromise
    }
    
    commit('SET_INITIALIZING', true)
    
    const promise = (async () => {
      try {
        const isAuthenticated = await authService.initializeAuth()
        
        if (isAuthenticated) {
          const user = await authService.getCurrentUser()
          
          if (user) {
            commit('RESTORE_SESSION', user)
            
            // Synchroniser avec le module user
            await dispatch('user/setCurrentUser', user, { root: true })
            
            // Initialiser les paramètres de session (team, manager) après restauration de session
            try {
              const params = {}
              
              // Ajouter le team si disponible
              if (user.team && user.team.id) {
                params.team = user.team.id
              }
              
              // Ajouter le manager (user.id) si disponible
              if (user.id) {
                params.manager = user.id
              }
              
              // Ajouter les paramètres par défaut
              params.status = 'vacant'
              params.hasPlanification = 'ok'
              params.hasNotPlanification = 'ok'
              
              // Sauvegarder dans sessionStorage
              if (Object.keys(params).length > 0) {
                sessionStorage.setItem('preservedRouteParams', JSON.stringify(params))
              }
            } catch (sessionError) {
              console.error('Erreur lors de l\'initialisation des paramètres de session:', sessionError)
            }
            
            return true
          }
        }
        
        commit('LOGOUT')
        return false
      } catch (error) {
        console.error('Erreur lors de l\'initialisation de l\'authentification:', error)
        commit('LOGOUT')
        // Nettoyer également le module user
        dispatch('user/clearUser', null, { root: true }).catch(() => {})
        return false
      } finally {
        commit('SET_INITIALIZING', false)
        commit('SET_INITIALIZE_PROMISE', null)
      }
    })()
    
    commit('SET_INITIALIZE_PROMISE', promise)
    return promise
  },
  
  async refreshUserData({ commit, dispatch }) {
    try {
      const user = await authService.fetchCurrentUser()
      commit('SET_USER', user)
      
      // Synchroniser avec le module user
      await dispatch('user/setCurrentUser', user, { root: true })
      
      return user
    } catch (error) {
      console.error('Erreur lors de la récupération des données utilisateur:', error)
      
      // Vérifier si c'est une erreur réseau (API inaccessible) ou une erreur d'authentification
      const isNetworkError = error.message.includes('Failed to fetch') || 
                            error.message.includes('NetworkError') ||
                            error.message.includes('ERR_CONNECTION') ||
                            !error.message.includes('401') && !error.message.includes('403')
      
      if (isNetworkError) {
        // Si c'est une erreur réseau, on garde l'utilisateur connecté avec les données en cache
        console.warn('API inaccessible, conservation de la session utilisateur')
        const cachedUser = await authService.getCurrentUser()
        if (cachedUser) {
          commit('SET_USER', cachedUser)
          // Synchroniser avec le module user
          await dispatch('user/setCurrentUser', cachedUser, { root: true })
          return cachedUser
        }
      }
      
      // Seulement en cas d'erreur d'authentification réelle, déconnecter l'utilisateur
      await authService.logout()
      commit('LOGOUT')
      // Nettoyer également le module user
      dispatch('user/clearUser', null, { root: true }).catch(() => {})
      throw error
    }
  },
  
  grantAccess({ commit }, user) {
    commit('GRANT_ACCESS', user)
  },
  
  denyAccess({ commit }, errorMessage) {
    commit('DENY_ACCESS', errorMessage)
  },
  
  async enterApp({ dispatch }) {
    // Initialiser l'authentification au démarrage de l'app
    return await dispatch('initializeAuth')
  },
  
  enterLogin({ commit }) {
    // Actions à effectuer lors de l'entrée sur la page de login
    commit('RESET_TOOLTIP')
    commit('RESET_HEIGHT')
  },
  
  extendHeight({ commit }) {
    commit('EXTEND_HEIGHT')
  },
  
  resetHeight({ commit }) {
    commit('RESET_HEIGHT')
  }
}

const getters = {
  isLoggedIn: state => state.isLoggedIn,
  username: state => state.username,
  password: state => state.password,
  tooltip: state => state.tooltip,
  usernameError: state => state.usernameError,
  passwordError: state => state.passwordError,
  loading: state => state.loading,
  isHeightExtended: state => state.isHeightExtended,
  isSubmitDisabled: state => !state.username || !state.password
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
